import {cloneDeep} from 'lodash';
import React, {type ReactNode} from 'react';
import {Card} from 'semantic-ui-react';

interface BokehViewerProps {
  headerElements?: ReactNode;
  notFoundElements?: ReactNode;
  contentNotFound?: boolean;
  bokehJson?: any;
  /** Parent-owned box; when set, the plot is resized to fill it (like PlotlyCard). */
  width?: number;
  height?: number;
}

// Match PlotlyCard mins so tiny boxes don't produce unreadable embeds.
const BOKEH_MIN_WIDTH = 300;
const BOKEH_MIN_HEIGHT = 300;
// Outer wrapper padding; subtract so the plot canvas fits the allocated box.
const BOKEH_PAD_PX = 10;

function isPlotModel(node: any): boolean {
  if (node == null || typeof node !== 'object') {
    return false;
  }
  const name = node.name ?? node.type;
  if (name === 'Figure' || name === 'Plot') {
    return true;
  }
  return typeof node.type === 'string' && node.type.includes('Plot');
}

function applySizeToAttrs(
  attrs: Record<string, unknown>,
  width: number,
  height: number
): void {
  // Bokeh 2 used plot_width/plot_height; Bokeh 3 uses width/height.
  if ('plot_width' in attrs || 'plot_height' in attrs) {
    attrs.plot_width = width;
    attrs.plot_height = height;
  }
  if ('width' in attrs || 'height' in attrs) {
    attrs.width = width;
    attrs.height = height;
  }
}

/** Mutates a cloned Bokeh doc so root plots fill the parent box (PlotlyCard parity). */
export function applyBokehBoxSize(
  bokehJson: any,
  width: number,
  height: number
): any {
  const copy = cloneDeep(bokehJson);
  const plotWidth = Math.max(BOKEH_MIN_WIDTH, width);
  const plotHeight = Math.max(BOKEH_MIN_HEIGHT, height);
  const roots = copy?.roots;

  if (Array.isArray(roots)) {
    // Bokeh 3 document.to_json(): roots is a list of models.
    for (const root of roots) {
      if (isPlotModel(root) && root.attributes != null) {
        applySizeToAttrs(root.attributes, plotWidth, plotHeight);
      }
    }
  } else if (roots?.references != null) {
    // Bokeh 2 style: roots.references holds models keyed by type.
    for (const ref of roots.references) {
      if (isPlotModel(ref) && ref.attributes != null) {
        applySizeToAttrs(ref.attributes, plotWidth, plotHeight);
      }
    }
  }

  return copy;
}

// This injects a bokeh library fetch based on the version of the bokeh content. a bit hacky,
// but I think the only way to ensure compatibility with the user's python bokeh version and the
// JS version.
const loadBokehLibrary = (
  version: string = '2.2.3',
  callback: () => void = () => {}
) => {
  const scriptID = '__bokeh_lib_injection__';
  const existingScript = document.getElementById(scriptID);
  if (!existingScript) {
    const script = document.createElement('script');
    script.src = `https://cdn.bokeh.org/bokeh/release/bokeh-${version}.min.js`;
    script.id = scriptID;
    document.body.appendChild(script);
    script.addEventListener('load', callback);
  } else if (!(window as any).Bokeh) {
    existingScript.addEventListener('load', callback);
  } else {
    callback();
  }
};

const BokehViewer = (props: BokehViewerProps) => {
  const [libraryLoaded, setLibraryLoaded] = React.useState<boolean>(false);
  if (!props.bokehJson?.version) {
    return <>-</>;
  } else if (!libraryLoaded) {
    loadBokehLibrary(props.bokehJson.version, () => {
      setLibraryLoaded(true);
    });
    return <></>;
  } else {
    // See https://github.com/bokeh/bokeh/blob/55a0a5d33376abb029506e7c1facc85a2b2a2fa7/bokehjs/src/lib/core/logging.ts#L12
    (window as any).Bokeh.logger.set_level('error');
    return <BokehViewerInner {...props} />;
  }
};

const BokehViewerInner = (props: BokehViewerProps) => {
  const bokehDivRef = React.useRef<HTMLDivElement>(null);
  // embed_item resolves the mount node via document.getElementById, so each
  // viewer instance needs a document-unique id when several tiles are on screen.
  const reactId = React.useId();
  const bokehDivId = `_bokeh_${reactId.replace(/:/g, '')}`;

  React.useEffect(() => {
    if (!props.bokehJson) {
      return;
    }
    if (bokehDivRef.current) {
      bokehDivRef.current.innerHTML = '';
    }
    // Simple Bokeh objects will not have multiple roots. So
    // we can just use the first root_id. This was discovered
    // by unit tests creating simple Bokeh objects.
    let rootId: string | number = 0;
    if (props.bokehJson.roots?.root_ids != null) {
      rootId = props.bokehJson.roots.root_ids[0];
    } else if (
      Array.isArray(props.bokehJson.roots) &&
      props.bokehJson.roots[0]?.id
    ) {
      rootId = props.bokehJson.roots[0].id;
    }

    let doc = props.bokehJson;
    if (props.width != null && props.height != null) {
      // Size the plot itself to the parent box (same contract as PlotlyCard),
      // accounting for this wrapper's padding so we don't overflow the tile.
      doc = applyBokehBoxSize(
        props.bokehJson,
        props.width - BOKEH_PAD_PX * 2,
        props.height - BOKEH_PAD_PX * 2
      );
    }

    (window as any).Bokeh.embed.embed_item({
      doc,
      root_id: rootId,
      target_id: bokehDivId,
    });
    // Re-embed when the parent box changes so tiles resize with column count.
  }, [props.bokehJson, bokehDivId, props.width, props.height]);

  return (
    <div
      // Containing block for Bokeh's absolutely-positioned canvas chrome.
      // Padding must stay in sync with BOKEH_PAD_PX (used when sizing the plot).
      className="relative box-border max-w-full overflow-hidden p-[10px] w-full"
      style={props.width != null ? {width: props.width} : undefined}>
      {/* Content-sized card like PlotlyCard — vertical centering stays with the tile. */}
      <Card className="bokeh-card w-full">
        {props.headerElements}

        {!!props.contentNotFound || !props.bokehJson ? (
          props.notFoundElements ? (
            props.notFoundElements
          ) : null
        ) : (
          <div ref={bokehDivRef} id={bokehDivId} className="relative w-full" />
        )}
      </Card>
    </div>
  );
};

export default BokehViewer;
